import { motion } from "framer-motion";
import { ArrowRight, Clock, Flower2, Soup, UtensilsCrossed } from "lucide-react";

const KITCHENS = [
    {
        name: "Esak",
        tag: "Now Serving",
        tagline: "Flavours for the modern heart",
        desc: "Familiar Indian flavours with a fresh twist, light, colourful, and full of love. Tradition, reimagined for today's heart.",
        icon: Flower2,
        logo: "/brand/esak-logo-pink.webp",
        accent: "#E50046",
        bg: "#F8F6F0",
        live: true,
        testid: "kitchen-card-esak",
    },
    {
        name: "South Indian",
        tag: "Coming Soon",
        tagline: "Coastal comfort, on its way",
        desc: "Crisp dosas, slow stews and filter coffee rituals, the warmth of the South is finding its corner under our trees.",
        icon: Soup,
        accent: "#00958C",
        bg: "#FFFFFF",
        live: false,
        testid: "kitchen-card-south-indian",
    },
    {
        name: "Pan Asian",
        tag: "Coming Soon",
        tagline: "A new avenue of taste",
        desc: "From fragrant broths to wok-tossed evenings, a journey across Asia is being plated for you.",
        icon: UtensilsCrossed,
        accent: "#1C8DB9",
        bg: "#FFFFFF",
        live: false,
        testid: "kitchen-card-pan-asian",
    },
];

export const Kitchens = () => {
    return (
        <section id="kitchens" className="py-24 md:py-32 px-6 md:px-10 bg-[#262055]" data-testid="kitchens-section">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="max-w-2xl"
                >
                    <p className="font-serif-brand italic text-xl text-[#F0841B] mb-4">Three kitchens, one shared table</p>
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-white leading-tight" data-testid="kitchens-headline">
                        Every avenue leads<br />to a new flavour.
                    </h2>
                </motion.div>

                <div className="mt-16 grid md:grid-cols-3 gap-6">
                    {KITCHENS.map((k, i) => (
                        <motion.article
                            key={k.name}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.12 }}
                            className="relative rounded-[2rem] p-8 md:p-10 flex flex-col min-h-[380px] overflow-hidden hover:-translate-y-1.5 transition-transform duration-300"
                            style={{ backgroundColor: k.bg }}
                            data-testid={k.testid}
                        >
                            <span
                                className="inline-flex items-center gap-1.5 self-start rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white"
                                style={{ backgroundColor: k.accent }}
                                data-testid={`${k.testid}-badge`}
                            >
                                {!k.live && <Clock size={12} />}
                                {k.tag}
                            </span>
                            {k.logo ? (
                                <img src={k.logo} alt={`${k.name}, ${k.tagline}`} className="mt-8 h-16 w-auto self-start" data-testid={`${k.testid}-logo`} />
                            ) : (
                                <>
                                    <k.icon size={40} className="mt-8" style={{ color: k.accent }} />
                                    <h3 className="mt-4 text-3xl font-bold tracking-tight text-[#262055]">{k.name}</h3>
                                    <p className="font-serif-brand italic text-lg mt-1" style={{ color: k.accent }}>{k.tagline}</p>
                                </>
                            )}
                            {k.logo && <div className="mt-4" />}
                            <p className="mt-4 text-sm leading-relaxed text-[#262055]/65 flex-1">{k.desc}</p>
                            {k.live ? (
                                <a
                                    href="#esak"
                                    data-testid="kitchen-esak-cta"
                                    className="mt-6 inline-flex items-center gap-2 font-semibold text-sm group"
                                    style={{ color: k.accent }}
                                >
                                    Discover Esak
                                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                                </a>
                            ) : (
                                <span className="mt-6 text-xs font-semibold uppercase tracking-widest text-[#262055]/40" data-testid={`${k.testid}-soon-note`}>
                                    Unveiling soon at the Avenue
                                </span>
                            )}
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};
